import type { PublicTrace, PublicTraceEntry } from '@onefootprint/request-types/dashboard';

type RulePathStep = {
  id: string;
  kind: 'conditional' | 'rules';
  name?: string;
  /** The expressions of the rules that fired on the path the node took; empty when none did. */
  conditions: string[];
  /** Where the node went: a conditional's branch, or the value a rules node assigned. */
  outcome: string;
  /** The data field a rules node assigns `outcome` to, such as `kyc_result`. */
  field?: string;
  /** A conditional that left by a non-default branch with nothing triggered: a sandbox outcome. */
  isNotEvaluated: boolean;
};

// A conditional or rules node counts only once it executed properly.
const isExecutedRuleEntry = (entry: PublicTraceEntry) =>
  (entry.nodeKind === 'conditional' || entry.nodeKind === 'rules') &&
  (entry.result.signalKind === 'proceed' || entry.result.signalKind === 'halt');

// Two rules can share an expression; listed or counted twice, it reads as one more rule than the reader sees.
const unique = (expressions: string[]) => [...new Set(expressions)];

/** The rule nodes an onboarding went through, in order, with what fired in each. */
const getRulePathSteps = (trace: PublicTrace): RulePathStep[] =>
  trace.entries.filter(isExecutedRuleEntry).flatMap((entry): RulePathStep[] => {
    const { context } = entry;
    const node = trace.graph.nodes.find(({ id }) => id === entry.decisionNodeId);
    if (context?.kind === 'conditional') {
      const conditions = unique(
        context.branchResults
          .filter(({ branchName }) => branchName === context.outcomeBranchName)
          .flatMap(({ results }) => results)
          .filter(({ result }) => result)
          .map(({ ruleExpression }) => ruleExpression),
      );
      const defaultBranch = node?.kind === 'conditional' ? node.defaultBranch?.name : undefined;
      return [
        {
          id: entry.decisionNodeId,
          kind: 'conditional',
          name: node?.kind === 'conditional' ? node.name : undefined,
          conditions,
          outcome: context.outcomeBranchName,
          isNotEvaluated: !conditions.length && context.outcomeBranchName !== defaultBranch,
        },
      ];
    }
    if (context?.kind === 'rules') {
      const rulesNode = node?.kind === 'rules' ? node : undefined;
      // Several groups can assign the same value, and the one that fired may not be the first.
      const conditions = unique(
        (rulesNode?.groups ?? [])
          .filter(({ value }) => value === context.assignedValue)
          .flatMap(({ conditions: groupConditions }) => groupConditions)
          .filter(({ id }) => context.groupResults.find(({ ruleId }) => ruleId === id)?.result)
          .map(({ expression }) => expression),
      );
      return [
        {
          id: entry.decisionNodeId,
          kind: 'rules',
          name: rulesNode?.name,
          conditions,
          outcome: context.assignedValue,
          field: rulesNode?.key,
          isNotEvaluated: false,
        },
      ];
    }
    return [];
  });

export type { RulePathStep };
export default getRulePathSteps;
